import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { saveSubscription } from '../../lib/payments';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { trackEvent, trackPurchase } from '../../lib/analytics';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUserProfile } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processPayment = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        // Extract payment details from URL params
        const paymentId = searchParams.get('razorpay_payment_id');
        const orderId = searchParams.get('razorpay_order_id');
        const signature = searchParams.get('razorpay_signature') || '';
        const planId = searchParams.get('notes[plan_id]');
        const amount = parseInt(searchParams.get('amount') || '0', 10);

        if (!paymentId || !orderId || !planId || !amount) {
          throw new Error('Invalid payment details');
        }

        console.log('Processing payment:', { 
          paymentId, 
          orderId, 
          planId, 
          amount,
          hasSignature: !!signature 
        });

        // Save subscription in database
        await saveSubscription(
          user.phoneNumber,
          planId,
          paymentId,
          orderId,
          amount / 100 // Convert from paise to rupees
        );

        // Track purchase event in GA4
        trackPurchase(orderId, amount / 100, 'INR', [
          {
            item_id: planId,
            item_name: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Subscription`,
            price: amount / 100,
            quantity: 1
          }
        ]);

        // Refresh user profile to get updated premium status
        await refreshUserProfile();

        setProcessing(false);
      } catch (error: any) {
        console.error('Error processing payment:', error);
        trackEvent('payment_error', 'error', error.message || 'Unknown error');
        setError(error.message || 'Failed to process payment. Please contact support.');
        setProcessing(false);
      }
    };

    processPayment();
  }, [user, searchParams, navigate, refreshUserProfile]);

  if (processing) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium">Processing your payment...</p>
          <p className="text-sm text-neutral-600 mt-2">Please wait while we activate your premium subscription</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto">
                <Star className="h-8 w-8 text-error-600" />
              </div>
            </div>
            <h1 className="text-xl font-semibold text-error-900 mb-2">Payment Failed</h1>
            <p className="text-neutral-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  trackEvent('retry_payment', 'click', 'payment_error');
                  navigate('/premium');
                }} 
                fullWidth
              >
                Try Again
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  trackEvent('navigation', 'click', 'error_to_dashboard');
                  navigate('/');
                }} 
                fullWidth
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
            </div>
            <h1 className="text-xl font-semibold text-success-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-neutral-600 mb-6">
              Your premium subscription has been activated. Enjoy unlimited access to all features!
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  trackEvent('navigation', 'click', 'success_to_dashboard');
                  navigate('/');
                }} 
                fullWidth
              >
                Start Exploring
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  trackEvent('navigation', 'click', 'success_to_profile');
                  navigate('/profile');
                }} 
                fullWidth
              >
                View Subscription Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;